import React, { useCallback, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import AccountLayout from 'components/layout/account';
import LoadingAnimation from 'components/loading-animation';
import ModalOrderUnavailable from 'components/modal-order-unavailable';
import { BackArrowHistory } from 'components/navigation/back-arrow';
import { RecentItemsCarousel } from 'components/recent-items-carousel';
import { RefreshButton } from 'components/refresh-button/';
import useEffectOnce from 'hooks/use-effect-once';
import { useRecentItemsAvailable } from 'hooks/use-recent-items-available';
import { useUserOrders } from 'hooks/use-user-orders';
import AuthRequired from 'pages/authentication/auth-required';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { primitive } from 'styles/constants/primitives';
import { routes } from 'utils/routing';

import { ORDER_CONTAINERS_SECTION_SPACING } from './constants';
import { DetailsModal } from './details-modal';
import Order from './order';
import OrdersEmpty from './orders-empty';

export enum MODAL_TYPE {
  SHOW_DETAILS = 'showDetails',
  TRACK_ORDER = 'trackOrder',
}

const OrdersContainer = styled.div`
  margin-left: auto;
  margin-right: auto;
  max-width: ${Styles.layout.sectionMaxWidth};
  padding-bottom: 0;
  padding-top: 0;
  width: calc(100% - ${ORDER_CONTAINERS_SECTION_SPACING});
  & > div {
    padding-top: 0px;
  }
`;

const OrdersEmptyWrapper = styled.div`
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const OrdersPanel = styled.div`
  width: 100%;
  ${Styles.desktop`
    padding-top: ${primitive.$spacing7};
  `};
`;

const OfferDisclaimer = styled.div`
  text-align: center;
`;

const AccountOrderWrapper = styled.div`
  padding: ${primitive.$spacing5} 0 ${primitive.$spacing11};
  ${Styles.desktop`
    padding-top: 150px;
  `}
`;

const AccountOrders: React.FC = () => {
  const navigate = useNavigate();
  const { refreshCurrentUserWithNewSession } = useAuthContext();
  const params = useParams();
  const rbiOrderId = params['*'];
  const [detailsModal, setDetailsModal] = useState(rbiOrderId || '');
  const [showOrderUnavailableModal, setShowOrderUnavailableModal] = useState(false);
  const { loading, orders, refetch } = useUserOrders();
  const { unavailableCartEntries } = useOrderContext();
  const handleDisplayDetailsModal = useCallback(orderId => setDetailsModal(orderId), []);
  const handleOnModalDismiss = useCallback(() => setDetailsModal(''), []);
  const [modalType, setModalType] = useState<string>(MODAL_TYPE.SHOW_DETAILS);
  const { formatMessage } = useIntl();
  const showItems = useRecentItemsAvailable();
  const theme = useTheme();
  const onRefreshClick = useCallback(() => {
    refetch();
  }, [refetch]);
  const enableRecentItemsAddToCart = useFlag(LaunchDarklyFlag.ENABLE_RECENT_ITEMS_ADD_TO_CART);

  useEffectOnce(() => {
    refreshCurrentUserWithNewSession();
  });

  useEffect(() => {
    if (unavailableCartEntries.length) {
      setShowOrderUnavailableModal(true);
    }
  }, [showOrderUnavailableModal, setShowOrderUnavailableModal, unavailableCartEntries.length]);

  const handleModalDismiss = useCallback(() => {
    setShowOrderUnavailableModal(false);
    navigate(routes.cart);
  }, [navigate]);

  const refreshButton = (
    <RefreshButton onClick={onRefreshClick}>{formatMessage({ id: 'refresh' })}</RefreshButton>
  );
  const hasOrders = !!orders.length;
  return (
    <AccountOrderWrapper>
      <AuthRequired>
        <Helmet title={formatMessage({ id: 'recentOrders' })} />
        {showItems && (
          <OrdersContainer>
            <BackArrowHistory />
            {enableRecentItemsAddToCart && (
              <>
                <AccountLayout
                  header={formatMessage({ id: 'recentItems' })}
                  fullWidth
                  mobileContainerPadding="1.25rem 0 0 0"
                  desktopContainerPadding="0"
                >
                  <RecentItemsCarousel
                    locationName="Recent Orders Page"
                    containerHasTopPadding={false}
                  />
                </AccountLayout>
              </>
            )}
          </OrdersContainer>
        )}

        <AccountLayout
          header={hasOrders ? formatMessage({ id: 'recentOrders' }) : null}
          fullWidth
          mobileContainerPadding="1.25rem 0 0 0"
          desktopContainerPadding="0"
        >
          {loading ? (
            <LoadingAnimation fillColor={theme.token('icon-pattern-active')} />
          ) : (
            <OrdersContainer>
              {hasOrders ? (
                <>
                  <OfferDisclaimer>
                    {formatMessage({ id: 'recentOrdersDisclaimer' })}
                  </OfferDisclaimer>
                  {refreshButton}
                  <OrdersPanel>
                    {orders.map(order => (
                      <Order
                        key={order.rbiOrderId}
                        order={order}
                        handleDisplayDetailsModal={handleDisplayDetailsModal}
                        setModalType={setModalType}
                      />
                    ))}
                  </OrdersPanel>
                </>
              ) : (
                <OrdersEmptyWrapper>
                  <OrdersEmpty>{refreshButton}</OrdersEmpty>
                </OrdersEmptyWrapper>
              )}
            </OrdersContainer>
          )}
        </AccountLayout>
        {detailsModal && (
          <DetailsModal
            rbiOrderId={detailsModal}
            onModalDismiss={handleOnModalDismiss}
            type={modalType}
            setModalType={setModalType}
          />
        )}
        {showOrderUnavailableModal && (
          <ModalOrderUnavailable onDismiss={handleModalDismiss} items={unavailableCartEntries} />
        )}
      </AuthRequired>
    </AccountOrderWrapper>
  );
};

export default AccountOrders;
